import { Box, IconButton, Stack, Typography } from '@mui/material';
import { FilterKeys, GetMiniListQueryFilters, useGetMiniListQuery } from '@schooly/api';
import { SimpleListResult } from '@schooly/api';
import { MINI_LIST } from '@schooly/constants/test-ids';
import { useFlag } from '@schooly/hooks/use-flag';
import { useInfiniteScroll } from '@schooly/hooks/use-infinite-scroll';
import { Loading, PlusIcon, RollBackIcon } from '@schooly/style';
import React, { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { getSchoolUserId } from '../../../helpers/school';
import { useSchool } from '../../../hooks/useSchool';
import { GroupPreviewModalConductSearchInput } from '../../../pages/Groups/GroupPreviewModal/GroupPreviewModalConduct/GroupPreviewModalConductSearchInput';
import { SimpleButton } from '../../uikit/SimpleButton/SimpleButton';
import { ModalContent, ModalPanel } from '../../uikit-components/Modal/Modal.styled';
import { ModalHeader } from '../../uikit-components/Modal/ModalHeader';
import { NoSearchResultsFound } from '../NoSearchResultsFound/NoSearchResultsFound';
import PersonCardSelectable from '../PersonCard/PersonCardSelectable';
import { ProfileAccordion } from '../ProfileAccordion/ProfileAccordion';
import { AddStaffFilters, AddStaffFiltersKey, AddStaffFiltersPopup } from './AddStaffFiltersPopup';
import { InlineFiltersTags } from './InlineFiltersTags';

export interface AddStaffExternalSidebarProps {
  filtersAvailable?: AddStaffFiltersKey[];
  staff: SimpleListResult[];
  onStaffClick: (user?: SimpleListResult | SimpleListResult[]) => void;
  initialFilters?: GetMiniListQueryFilters;
  deleteHistoryStaff?: SimpleListResult[];
}

const addStaffFilters: GetMiniListQueryFilters = {
  [FilterKeys.Status]: [],
  [FilterKeys.House]: [],
  [FilterKeys.Subject]: [],
};

const DEFAULT_FILTERS_AVAILABLE: AddStaffFiltersKey[] = [
  FilterKeys.Status,
  FilterKeys.House,
  FilterKeys.Subject,
];

export const AddStaffExternalSidebar: React.FC<AddStaffExternalSidebarProps> = ({
  filtersAvailable = DEFAULT_FILTERS_AVAILABLE,
  staff: selectedStaff,
  onStaffClick,
  initialFilters = addStaffFilters,
  deleteHistoryStaff,
}) => {
  const { formatMessage, $t } = useIntl();
  const { schoolId = '' } = useSchool();
  const [filtersModalVisible, openFilterModal, closeFiltersModal] = useFlag(false);

  const { data, params, setParams, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetMiniListQuery(
      {
        schoolId,
        query: '',
        type: 'staff',
        filters: initialFilters,
      },
      { refetchOnMount: 'always' },
    );

  useEffect(() => {
    setParams((p) => ({ ...p, filters: { ...initialFilters, ...p.filters } }));
  }, [initialFilters, setParams]);

  const filteredList = useMemo(() => {
    const selectedUserIds = selectedStaff.map(getSchoolUserId);
    const entries =
      data?.pages.reduce<SimpleListResult[]>((prev, curr) => {
        prev.push(
          ...curr.results.filter(
            (user) =>
              !selectedUserIds.includes(getSchoolUserId(user)) &&
              !deleteHistoryStaff?.some((u) => u.relation_id === user.relation_id),
          ),
        );
        return prev;
      }, []) ?? [];

    return entries;
  }, [data?.pages, deleteHistoryStaff, selectedStaff]);

  const loaderRef = useInfiniteScroll(isLoading || isFetchingNextPage, fetchNextPage, hasNextPage);

  const handleSetSearchText = useCallback(
    (query: string) => {
      setParams((params) => ({ ...params, query }));
    },
    [setParams],
  );
  const handleSetFilters = useCallback(
    (filters: AddStaffFilters) => {
      setParams((params) => ({ ...params, filters: { ...params.filters, ...filters } }));
    },
    [setParams],
  );

  const handleResetFilters = useCallback(() => {
    setParams((params) => ({ ...params, filters: initialFilters }));
  }, [initialFilters, setParams]);

  const renderContent = () => {
    if (isLoading) {
      return <Loading />;
    }

    if (!filteredList.length && !hasNextPage) {
      return <NoSearchResultsFound />;
    }

    return (
      <>
        {!!deleteHistoryStaff?.length && (
          <Stack pb={2} pt={1}>
            <ProfileAccordion title={$t({ id: 'groups-deleteHistory' })} expandable expandedValue>
              {deleteHistoryStaff.map((user) => (
                <PersonCardSelectable
                  isUsernameClickable
                  isUsernameLinkView
                  key={user.relation_id}
                  user={user}
                  userType="staff"
                  onClick={() => {
                    onStaffClick(user);
                  }}
                  isListItem
                  renderCustomIcon={
                    <IconButton inverse>
                      <RollBackIcon />
                    </IconButton>
                  }
                />
              ))}
            </ProfileAccordion>
          </Stack>
        )}
        {!!filteredList?.length && !!deleteHistoryStaff?.length && (
          <Typography variant="h4">
            <FormattedMessage id="membership-AvailableToAdd" />
          </Typography>
        )}
        {filteredList?.map((user) => (
          <PersonCardSelectable
            isUsernameClickable
            isUsernameLinkView
            key={user.relation_id}
            user={user}
            userType="student"
            onClick={() => onStaffClick(user)}
            isListItem
          ></PersonCardSelectable>
        ))}
        {hasNextPage && (
          <Box py={3}>
            <Loading />
            <div ref={loaderRef} />
          </Box>
        )}
      </>
    );
  };

  const hasFiltersApplied = filtersAvailable.some(
    (key) => !!params.filters[key as keyof typeof initialFilters]?.length,
  );

  return (
    <Stack sx={{ height: '100%' }} data-test-id={MINI_LIST.CONTAINER}>
      <ModalHeader title={formatMessage({ id: 'userType-staff-plural' })} active />
      <Box px={2.5} pt={2.5}>
        <Stack gap={1}>
          <GroupPreviewModalConductSearchInput
            value={params.query || ''}
            onChangeText={handleSetSearchText}
            hasFilters={hasFiltersApplied}
            onFiltersPress={filtersAvailable.length ? openFilterModal : undefined}
            placeholder={formatMessage(
              { id: 'groups-AvailableUsersSearch' },
              {
                userType: formatMessage({ id: 'userType-staff-plural' }).toLowerCase(),
              },
            )}
          />
          <InlineFiltersTags
            filtersAvailable={filtersAvailable}
            onResetFilters={handleResetFilters}
            onOpenFilters={openFilterModal}
            filters={params.filters}
          />
        </Stack>
        {filtersModalVisible && (
          <AddStaffFiltersPopup
            filtersAvailable={filtersAvailable}
            onClose={closeFiltersModal}
            defaultFilters={initialFilters}
            onSetFilters={handleSetFilters}
            filters={params.filters}
          />
        )}
      </Box>
      <ModalContent active>{renderContent()}</ModalContent>

      {!!filteredList.length && (
        <ModalPanel
          active
          withBorderTop
          sx={{
            px: 2.5,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 63,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              borderRadius: 1,
              width: '100%',
            }}
          >
            <SimpleButton
              startIcon={<PlusIcon />}
              onClick={() => onStaffClick(filteredList)}
              data-test-id={MINI_LIST.ADD_ALL_BTN}
            >
              <Typography variant="h3">
                <FormattedMessage id="groups-AddAllStaff" values={{ count: filteredList.length }} />
              </Typography>
            </SimpleButton>
          </Stack>
        </ModalPanel>
      )}
    </Stack>
  );
};
